import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Supplier } from '@/firebase/services/suppliers.service'
import { useCurrentOrganizationStore } from './currentOrganization'
import { useCostCenterStore } from './costCenter'
import { useCostTypeStore } from './costType'
import { BaseStore } from './baseStore'
import { where, type QueryConstraint } from 'firebase/firestore'

export interface Supplier {
  id?: string
  name: string
  document?: string
  email?: string
  phone?: string
  address?: {
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
  }
  bankInfo?: {
    bank?: string
    agency?: string
    account?: string
    accountType?: 'checking' | 'savings'
  }
  costCenters?: Array<{
    costCenterId: string
    costTypeId: string
    costCenterName: string
    costTypeName: string
  }>
  active?: boolean
  organizationId?: string
  createdAt?: string
  updatedAt?: string
}

class SupplierStore extends BaseStore {
  constructor() {
    super('suppliers')
  }

  async fetchSuppliers(organizationId: string) {
    if (!organizationId) {
      throw new Error('organizationId is required')
    }
    const filters: QueryConstraint[] = [
      where('organizationId', '==', organizationId),
      where('active', '==', true)
    ]
    return await this.fetchCollection<Supplier>(filters)
  }

  async addSupplier(supplier: Supplier) {
    if (!supplier.organizationId) {
      throw new Error('organizationId is required')
    }
    return await this.addDocument<Supplier>({
      ...supplier,
      active: true
    })
  }

  async updateSupplier(id: string, supplier: Partial<Supplier>) {
    return await this.updateDocument<Supplier>(id, supplier)
  }

  async deleteSupplier(id: string) {
    return await this.updateDocument(id, { active: false })
  }

  async addCostCenterToSupplier(
    supplierId: string,
    costCenterId: string,
    costTypeId: string,
    costCenterName: string,
    costTypeName: string
  ) {
    const supplier = await this.getDocument<Supplier>(supplierId)
    if (!supplier) {
      throw new Error('Fornecedor não encontrado')
    }

    if (!supplier.costCenters) {
      supplier.costCenters = []
    }

    const existingIndex = supplier.costCenters.findIndex(
      cc => cc.costCenterId === costCenterId && cc.costTypeId === costTypeId
    )

    if (existingIndex >= 0) {
      supplier.costCenters[existingIndex] = {
        costCenterId,
        costTypeId,
        costCenterName,
        costTypeName
      }
    } else {
      supplier.costCenters.push({
        costCenterId,
        costTypeId,
        costCenterName,
        costTypeName
      })
    }

    return await this.updateDocument(supplierId, {
      costCenters: supplier.costCenters
    })
  }

  async removeCostCenterFromSupplier(
    supplierId: string,
    costCenterId: string,
    costTypeId: string
  ) {
    const supplier = await this.getDocument<Supplier>(supplierId)
    if (!supplier || !supplier.costCenters) {
      throw new Error('Fornecedor não encontrado ou sem centros de custo')
    }

    supplier.costCenters = supplier.costCenters.filter(
      cc => !(cc.costCenterId === costCenterId && cc.costTypeId === costTypeId)
    )

    return await this.updateDocument(supplierId, {
      costCenters: supplier.costCenters
    })
  }
}

export const useSupplierStore = defineStore('supplier', () => {
  const suppliers = ref<Supplier[]>([])
  const loading = ref(false)
  const currentOrganizationStore = useCurrentOrganizationStore()
  const costCenterStore = useCostCenterStore()
  const costTypeStore = useCostTypeStore()
  const supplierStore = new SupplierStore()

  const fetchSuppliers = async () => {
    loading.value = true
    try {
      const organizationId = currentOrganizationStore.organizationId
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      // Buscar fornecedores
      const fetchedSuppliers = await supplierStore.fetchSuppliers(organizationId)
      console.log('Fetched suppliers:', fetchedSuppliers)
      
      // Para cada fornecedor, buscar os nomes dos centros de custo e tipos
      const suppliersWithCostInfo = await Promise.all(
        fetchedSuppliers.map(async (supplier) => {
          if (supplier.costCenters?.length) {
            try {
              // Buscar todos os centros de custo de uma vez
              const allCostCenters = await costCenterStore.fetchCostCenters(organizationId)
              const allCostTypes = await costTypeStore.fetchCostTypes(organizationId)
              
              console.log('All cost centers:', allCostCenters)
              console.log('All cost types:', allCostTypes)
              
              if (!allCostCenters || !allCostTypes) {
                console.error('Cost centers or types not found')
                return supplier
              }
              
              const costCentersWithNames = supplier.costCenters.map(cc => {
                const costCenter = allCostCenters.find(c => c.id === cc.costCenterId)
                const costType = allCostTypes.find(t => t.id === cc.costTypeId)
                
                console.log('Found cost center:', costCenter)
                console.log('Found cost type:', costType)
                
                return {
                  ...cc,
                  costCenterName: costCenter?.name || 'Centro de custo não encontrado',
                  costTypeName: costType?.name || 'Tipo não encontrado'
                }
              })
              
              supplier.costCenters = costCentersWithNames
            } catch (error) {
              console.error('Error fetching cost info:', error)
            }
          }
          return supplier
        })
      )

      suppliers.value = suppliersWithCostInfo
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const addSupplier = async (supplier: Supplier) => {
    try {
      loading.value = true
      const newSupplier = await supplierStore.addSupplier(supplier)
      if (newSupplier) {
        suppliers.value.push(newSupplier)
      }
      return newSupplier
    } finally {
      loading.value = false
    }
  }

  const updateSupplier = async (id: string, supplier: Partial<Supplier>) => {
    try {
      loading.value = true
      const updatedSupplier = await supplierStore.updateSupplier(id, supplier)
      if (updatedSupplier) {
        const index = suppliers.value.findIndex(s => s.id === id)
        if (index !== -1) {
          suppliers.value[index] = { ...suppliers.value[index], ...updatedSupplier }
        }
      }
      return updatedSupplier
    } finally {
      loading.value = false
    }
  }

  const deleteSupplier = async (id: string) => {
    try {
      loading.value = true
      await supplierStore.deleteSupplier(id)
      const index = suppliers.value.findIndex(s => s.id === id)
      if (index !== -1) {
        suppliers.value.splice(index, 1)
      }
    } finally {
      loading.value = false
    }
  }

  const addCostCenterToSupplier = async (
    supplierId: string,
    costCenterId: string,
    costTypeId: string,
    costCenterName: string,
    costTypeName: string
  ) => {
    try {
      loading.value = true
      const updatedSupplier = await supplierStore.addCostCenterToSupplier(
        supplierId,
        costCenterId,
        costTypeId,
        costCenterName,
        costTypeName
      )
      if (updatedSupplier) {
        const index = suppliers.value.findIndex(s => s.id === supplierId)
        if (index !== -1) {
          suppliers.value[index] = updatedSupplier
        }
      }
      return updatedSupplier
    } finally {
      loading.value = false
    }
  }

  const removeCostCenterFromSupplier = async (
    supplierId: string,
    costCenterId: string,
    costTypeId: string
  ) => {
    try {
      loading.value = true
      const updatedSupplier = await supplierStore.removeCostCenterFromSupplier(
        supplierId,
        costCenterId,
        costTypeId
      )
      if (updatedSupplier) {
        const index = suppliers.value.findIndex(s => s.id === supplierId)
        if (index !== -1) {
          suppliers.value[index] = updatedSupplier
        }
      }
      return updatedSupplier
    } finally {
      loading.value = false
    }
  }

  return {
    suppliers,
    loading,
    fetchSuppliers,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    addCostCenterToSupplier,
    removeCostCenterFromSupplier
  }
})
